import React, {
  createContext,
  useContext,
  useEffect,
  useState,
  ReactNode,
} from 'react';
import {
  EventsService,
  EventServiceProvider,
  createEventService,
} from '@/api/services/events';
import { logError } from '@/utils/logHandling';

interface EventsContextType {
  eventsService: EventsService | null;
  isInitialized: boolean;
}

const EventsContext = createContext<EventsContextType>({
  eventsService: null,
  isInitialized: false,
});

interface EventsProviderProps {
  children: ReactNode;
  provider: EventServiceProvider;
  onInitialized?: () => void;
  onError?: (error: Error) => void;
}

export const EventsProvider: React.FC<EventsProviderProps> = ({
  children,
  provider,
  onInitialized,
  onError,
}) => {
  const [eventsService, setEventsService] = useState<EventsService | null>(
    null,
  );
  const [isInitialized, setIsInitialized] = useState(false);

  useEffect(() => {
    try {
      const service = createEventService(provider);
      setEventsService(service);
      setIsInitialized(true);
      onInitialized?.();
    } catch (error) {
      logError('Error initializing events service:', error);
      onError?.(error as Error);
    }
  }, [provider, onInitialized, onError]);

  useEffect(() => {
    return () => {
      eventsService?.shutdown();
    };
  }, [eventsService]);

  const contextValue: EventsContextType = {
    eventsService,
    isInitialized,
  };

  return (
    <EventsContext.Provider value={contextValue}>
      {children}
    </EventsContext.Provider>
  );
};

const useEvents = (): EventsContextType => {
  const context = useContext(EventsContext);
  if (context === undefined) {
    logError('useEvents must be used within an EventsProvider');
    throw new Error('useEvents must be used within an EventsProvider');
  }
  return context;
};

export const useEventsService = (): EventsService | null => {
  const { eventsService, isInitialized } = useEvents();

  if (!isInitialized || !eventsService) {
    logError('Events service not yet initialized');
    return null;
  }

  return eventsService;
};
